import React, { useState } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Phone, MessageCircle, Mail, MapPin, Send } from 'lucide-react';
import { businessInfo } from '../mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        service: formData.service,
        message: formData.message
      };
      if (formData.email) payload.email = formData.email;

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Thank you! We will contact you shortly.');
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          message: ''
        });
      } else {
        toast.error('Failed to submit form. Please try calling us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try calling us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCall = () => {
    window.location.href = `tel:${businessInfo.phone}`;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${businessInfo.whatsapp}?text=Hi, I'm interested in your marble polishing services`, '_blank');
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Get Your Free Quote Today
          </h2>
          <p className="text-lg text-gray-600">
            Ready to transform your surfaces? Contact us now for a free consultation and quote
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="p-8 border-2 border-gray-100 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                  data-testid="lead-form-name-input"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  required
                  data-testid="lead-form-phone-input"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  data-testid="lead-form-email-input"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Needed *
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  data-testid="lead-form-service-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select a service</option>
                  <option value="marble-cutting-fixing">Marble Cutting & Fixing</option>
                  <option value="granite-cutting-fixing">Granite Cutting & Fixing</option>
                  <option value="italian-marble-laying">Italian Marble Laying & Finishing</option>
                  <option value="marble-granite-polishing">Marble & Granite Polishing</option>
                  <option value="interior-stone-inlay">Interior Stone & Inlay Work</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  rows={4}
                  data-testid="lead-form-message-input"
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="lead-form-submit-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Get Free Quote
                  </>
                )}
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="p-8 border-2 border-gray-100 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Phone</p>
                    <a href={`tel:${businessInfo.phone}`} data-testid="contact-phone-link" className="text-emerald-600 hover:text-emerald-700 font-medium">
                      {businessInfo.phone}
                    </a>
                    <p className="text-sm text-gray-600 mt-1">Mon-Sun: 7:00 AM - 9:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Email</p>
                    <a href={`mailto:${businessInfo.email}`} data-testid="contact-email-link" className="text-emerald-600 hover:text-emerald-700 font-medium">
                      {businessInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Location</p>
                    <p className="text-gray-700">{businessInfo.location}</p>
                    <p className="text-sm text-gray-600 mt-1">Serving all areas of Bangalore</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Quick Contact Buttons */}
            <div className="space-y-4">
              <Button
                onClick={handleCall}
                data-testid="contact-call-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: {businessInfo.phone}
              </Button>
              <Button
                onClick={handleWhatsApp}
                variant="outline"
                data-testid="contact-whatsapp-button"
                className="w-full border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 py-6 text-lg font-semibold"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat on WhatsApp
              </Button>
            </div>

            {/* Service Areas */}
            <Card className="p-6 bg-emerald-50 border-2 border-emerald-100">
              <p className="font-semibold text-gray-900 mb-2">Service Areas in Bangalore:</p>
              <p className="text-sm text-gray-700">
                Koramangala, Whitefield, Indiranagar, HSR Layout, Marathahalli, Electronic City, BTM Layout, Jayanagar, JP Nagar, Bannerghatta Road, Sarjapur Road, and all other areas in Bangalore.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
